<script setup lang="ts">
import { formatDate, formatNumberWithSpaces } from "@/composables/formatUtils";
import { useI18n } from "vue-i18n";
import type { CnmcRecipient, OutboundDestination } from "./cnmcTypes";

/**
 * The facts of a CNMC transfer or export, previewed or prepared: holder, recipient, destination,
 * total and filing deadline. The default slot adds facts after the holder, e.g. the route where the
 * caller does not name it elsewhere.
 */
defineProps<{
  holder: string;
  recipient: CnmcRecipient | null;
  destination: OutboundDestination | null;
  totalMwh: number;
  filingDeadline: string | null;
}>();

const { t } = useI18n();
</script>

<template>
  <dl class="row g-3 mb-0 outbound-facts">
    <div class="col-sm-6 col-lg-3">
      <dt class="small text-body-secondary fw-normal">
        {{ t("cnmc.outbound.review.holder") }}
      </dt>
      <dd class="fw-medium mb-0 text-break">{{ holder }}</dd>
    </div>
    <slot />
    <div class="col-sm-6 col-lg-3">
      <dt class="small text-body-secondary fw-normal">
        {{ t("cnmc.outbound.review.recipient") }}
      </dt>
      <dd class="fw-medium mb-0 text-break">
        <template v-if="recipient">
          {{ recipient.name }}
          <div class="small text-body-secondary">
            {{ recipient.r2 || recipient.eecs }}
          </div>
        </template>
        <template v-else>-</template>
      </dd>
    </div>
    <div v-if="destination" class="col-sm-6 col-lg-3">
      <dt class="small text-body-secondary fw-normal">
        {{ t("cnmc.outbound.review.destination") }}
      </dt>
      <dd class="fw-medium mb-0">
        {{ destination.country_name }}
        <div class="small text-body-secondary">
          {{
            t("cnmc.outbound.review.registry_code", {
              code: destination.registry_code,
            })
          }}
        </div>
      </dd>
    </div>
    <div class="col-sm-6 col-lg-3">
      <dt class="small text-body-secondary fw-normal">
        {{ t("cnmc.outbound.review.total") }}
      </dt>
      <dd class="fw-semibold mb-0">
        {{ formatNumberWithSpaces(totalMwh) }} MWh
      </dd>
    </div>
    <div v-if="filingDeadline" class="col-sm-6 col-lg-3">
      <dt class="small text-body-secondary fw-normal">
        {{ t("cnmc.filing_deadline") }}
      </dt>
      <dd class="fw-medium mb-0">{{ formatDate(filingDeadline) }}</dd>
    </div>
  </dl>
</template>

<style scoped lang="scss">
.outbound-facts {
  font-variant-numeric: tabular-nums;
}
</style>
